import { useEffect, useState, useCallback } from 'react'
import {
  apiAdminGetBets,
  apiAdminGetBetProducts,
  apiAdminRollbackBet,
  ApiRequestError,
  type AdminBet,
  type AdminBetProduct,
} from '@/lib/api'
import type { PaginatedResult, OtpConfirmation } from '@nexbit/types'
import { OtpConfirmDialog } from '@/components/OtpConfirmDialog'
import { formatCurrency } from '@/lib/utils'
import { format } from 'date-fns'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AlertCircle, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/contexts/language'
import type { Dict } from '@/lib/i18n'

function betStatusVariant(s: string): 'success' | 'destructive' | 'warning' | 'outline' {
  switch (s) {
    case 'won':       return 'success'
    case 'lost':      return 'destructive'
    case 'refunded':  return 'warning'
    case 'cancelled': return 'outline'
    default:          return 'outline'
  }
}

// Map raw API enum values to the active language.
function statusLabel(t: Dict, s: string): string {
  const map: Record<string, string> = {
    pending: t.bets.statusPending, won: t.bets.statusWon, lost: t.bets.statusLost,
    cancelled: t.bets.statusCancelled, refunded: t.bets.statusRefunded,
  }
  return map[s] ?? s
}
/**
 * Bet types across both books. The price games have four; a lottery has ten, and they
 * were reaching this function only to be upper-cased into 'RUN_TOP', which tells an
 * operator nothing.
 */
function betTypeLabel(t: Dict, bt: string): string {
  const map: Record<string, string> = {
    high: t.bets.typeHigh, low: t.bets.typeLow, odd: t.bets.typeOdd, even: t.bets.typeEven,
    '3top': '3 ตัวบน', '3tote': '3 ตัวโต๊ด', '3front': '3 ตัวหน้า', '3bottom': '3 ตัวล่าง', '2top': '2 ตัวบน', '2bottom': '2 ตัวล่าง',
    run_top: 'วิ่งบน', run_bottom: 'วิ่งล่าง',
    odd_even_top: 'บน คู่/คี่', high_low_top: 'บน สูง/ต่ำ',
    odd_even_bottom: 'ล่าง คู่/คี่', high_low_bottom: 'ล่าง สูง/ต่ำ',
    // KENO
    big: 'KENO ใหญ่', small: 'KENO เล็ก', dragon: 'KENO มังกร', tiger: 'KENO เสือ', dt_tie: 'KENO DT-เท่ากัน',
    up: 'KENO บน', down: 'KENO ล่าง', ud_tie: 'KENO บน/ล่างเท่ากัน',
    big_odd: 'KENO ใหญ่คี่', big_even: 'KENO ใหญ่คู่', small_odd: 'KENO เล็กคี่', small_even: 'KENO เล็กคู่',
    gold: 'KENO ทอง', wood: 'KENO ไม้', water: 'KENO น้ำ', fire: 'KENO ไฟ', earth: 'KENO ดิน',
  }
  return map[bt] ?? bt.toUpperCase()
}

const STATUSES = ['', 'pending', 'won', 'lost', 'cancelled', 'refunded'] as const

function RollbackDialog({
  bet,
  onClose,
  onProceed,
}: {
  bet: AdminBet | null
  onClose: () => void
  /** Hand off to the code dialog — reversing money is never a one-click action. */
  onProceed: (bet: AdminBet) => void
}) {
  const t = useT()

  return (
    <Dialog open={!!bet} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-sm">{t.bets.rollbackConfirm}</DialogTitle>
        </DialogHeader>

        {bet && (
          <div className="space-y-2 py-1">
            <div className="border border-border rounded-[var(--radius-sm)] p-3 space-y-1.5 bg-secondary/20">
              <Row label={t.bets.member} value={bet.memberUsername} />
              <Row label={t.bets.game}   value={bet.productName} />
              <Row label={t.bets.round}  value={`#${bet.roundNumber}`} />
              <Row label={t.bets.type}   value={betTypeLabel(t, bet.betType)} />
              <Row label={t.bets.amount} value={formatCurrency(bet.amount)} />
              <Row label={t.bets.status} value={statusLabel(t, bet.status)} />
              {bet.winAmount && <Row label={t.bets.winAmount} value={formatCurrency(bet.winAmount)} />}
            </div>
            <p className="font-mono text-xs text-warning">
              ⚠ การคืนเงินนี้จะคืนยอดเดิมพันให้สมาชิก และหักยอดรางวัล (ถ้ามี) กลับคืน
            </p>
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-8"
            onClick={onClose}
          >
            {t.common.cancel}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            className="text-xs h-8 gap-1.5"
            onClick={() => { if (bet) onProceed(bet) }}
          >
            <RotateCcw className="h-3 w-3" />
            {t.bets.rollback}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      <span className="font-mono text-xs text-foreground">{value}</span>
    </div>
  )
}

export default function AdminBetsPage() {
  const t = useT()
  const [data, setData] = useState<PaginatedResult<AdminBet> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [filterStatus, setFilterStatus] = useState('')
  const [filterProduct, setFilterProduct] = useState('')
  const [products, setProducts] = useState<AdminBetProduct[]>([])
  const [rollbackBet, setRollbackBet] = useState<AdminBet | null>(null)
  const [otpBet, setOtpBet] = useState<AdminBet | null>(null)

  const load = useCallback(async (p: number, s: string, product: string) => {
    setLoading(true)
    setError(null)
    try {
      setData(
        await apiAdminGetBets({
          page: p,
          limit: 20,
          status: s || undefined,
          product: product || undefined,
        }),
      )
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load(page, filterStatus, filterProduct) }, [load, page, filterStatus, filterProduct])

  // The filter offers what the table can contain, so it is read from the server rather
  // than hardcoded — a lottery added through the console shows up here on its own.
  useEffect(() => {
    void apiAdminGetBetProducts().then(setProducts).catch(() => setProducts([]))
  }, [])

  const totalPages = data ? Math.ceil(data.total / data.limit) : 0

  function handleRollbackDone(updated: AdminBet) {
    setData((prev) =>
      prev ? { ...prev, items: prev.items.map((b) => (b.id === updated.id ? updated : b)) } : prev,
    )
  }

  /**
   * A lottery bet has no rollback of its own: money is returned by cancelling the whole
   * งวด, which refunds every bet on it at once. Offering the button here would only ever
   * produce a 404, so it is not offered.
   */
  const canRollback = (b: AdminBet) =>
    b.kind === 'game' && b.status !== 'refunded' && b.status !== 'cancelled'

  const selectClass = cn(
    'h-8 rounded-[var(--radius-sm)] border border-input bg-transparent px-2',
    'font-mono text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring',
  )

  return (
    <div className="p-4 md:p-6 max-w-6xl">
      <div className="mb-5">
        <h1 className="text-lg font-semibold tracking-tight">{t.bets.title}</h1>
        <p className="text-sm text-muted-foreground mt-0.5">{t.bets.subtitle}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setPage(1) }}
          className={selectClass}
          aria-label={t.bets.filterStatus}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s ? statusLabel(t, s) : t.bets.allStatus}</option>
          ))}
        </select>
        <select
          value={filterProduct}
          onChange={(e) => { setFilterProduct(e.target.value); setPage(1) }}
          className={selectClass}
          aria-label="กรองตามประเภท"
        >
          <option value="">ทุกประเภท</option>
          {products.filter((x) => x.kind === 'draw').map((x) => (
            <option key={x.code} value={x.code}>{x.name}</option>
          ))}
          {products.filter((x) => x.kind === 'game').map((x) => (
            <option key={x.code} value={x.code}>{x.name}</option>
          ))}
        </select>
        <Input
          placeholder={t.bets.roundIdPlaceholder}
          className="h-8 w-48 font-mono text-xs"
          onKeyDown={(e) => {
            if (e.key === 'Enter') { setPage(1); void load(1, filterStatus, filterProduct) }
          }}
        />
      </div>

      {error && (
        <div
          className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4"
          role="alert"
        >
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
        <div className="px-4 py-2.5 bg-secondary flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
            {t.bets.history}
          </span>
          {data && !loading && (
            <span className="font-mono text-xs text-secondary-foreground/60">
              {data.total} {t.common.items}
            </span>
          )}
        </div>

        {loading ? (
          <div className="divide-y divide-border">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-5 w-14" />
                <Skeleton className="ml-auto h-4 w-20" />
              </div>
            ))}
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm text-muted-foreground">{t.bets.noBets}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.bets.member}</TableHead>
                <TableHead>{t.bets.gameRound}</TableHead>
                <TableHead>{t.bets.type}</TableHead>
                <TableHead className="text-right">{t.bets.amount}</TableHead>
                <TableHead className="text-right">{t.bets.winAmount}</TableHead>
                <TableHead>{t.bets.status}</TableHead>
                <TableHead className="text-right">{t.bets.time}</TableHead>
                <TableHead className="text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((b) => (
                <TableRow key={b.id}>
                  <TableCell className="font-mono text-xs font-semibold">
                    {b.memberUsername}
                  </TableCell>
                  <TableCell>
                    <p className="text-xs font-semibold">{b.productName}</p>
                    <p className="font-mono text-[10px] text-muted-foreground">
                      {/* A lottery bet is identified by its งวด and round, a price-game
                          bet by its round number — the same column, two vocabularies. */}
                      {b.drawDate
                        ? `${b.drawDate}${b.roundLabel ? ` · ${b.roundLabel}` : ''} · #${b.roundNumber}`
                        : `#${b.roundNumber}`}
                    </p>
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        'font-mono text-xs font-bold uppercase',
                        b.betType === 'high' ? 'text-success' :
                        b.betType === 'low'  ? 'text-destructive' :
                        b.betType === 'odd'  ? 'text-warning' :
                        'text-primary',
                      )}
                    >
                      {betTypeLabel(t, b.betType)}
                    </span>
                    {b.number && (
                      <p className="font-mono text-[11px] font-semibold tabular-nums text-foreground">
                        {b.number}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatCurrency(b.amount)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {b.winAmount && parseFloat(b.winAmount) > 0 ? (
                      <span className="text-success font-semibold">{formatCurrency(b.winAmount)}</span>
                    ) : (
                      <span className="text-muted-foreground/40">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={betStatusVariant(b.status)} className="text-[10px]">
                      {statusLabel(t, b.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <time className="font-mono text-xs text-muted-foreground">
                      {format(new Date(b.createdAt), 'dd/MM HH:mm')}
                    </time>
                  </TableCell>
                  <TableCell className="text-right">
                    {canRollback(b) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs gap-1 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setRollbackBet(b)}
                      >
                        <RotateCcw className="h-3 w-3" />
                        {t.bets.rollback}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {totalPages > 1 && !loading && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30">
            <span className="font-mono text-xs text-muted-foreground">
              {page} / {totalPages}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 p-0"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                aria-label="หน้าก่อน"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 p-0"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="หน้าถัดไป"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <RollbackDialog
        bet={rollbackBet}
        onClose={() => setRollbackBet(null)}
        onProceed={(b) => { setRollbackBet(null); setOtpBet(b) }}
      />

      {otpBet && (
        <OtpConfirmDialog
          purpose="bet.rollback"
          payload={{ params: { id: otpBet.id } }}
          title="ยืนยันการคืนเงินเดิมพัน"
          description={`คืนเงิน ${formatCurrency(otpBet.amount)} ให้ ${otpBet.memberUsername} ` +
            `(${otpBet.productName} รอบ #${otpBet.roundNumber})` +
            (otpBet.winAmount ? ` และหักรางวัล ${formatCurrency(otpBet.winAmount)} คืน` : '')}
          confirmLabel="คืนเงิน"
          danger
          onConfirm={async (otp: OtpConfirmation) => {
            handleRollbackDone(await apiAdminRollbackBet(otpBet.id, otp))
          }}
          onDone={() => setOtpBet(null)}
          onClose={() => setOtpBet(null)}
        />
      )}
    </div>
  )
}
